import { useState } from "react";
import { Moon, Plus, Check, Sunrise, X } from "lucide-react";
import { T } from "../theme";
import { Eyebrow, Card, Pill } from "../components";
import { createTask, toggleTask, moveTask, dropTask } from "../api/cloud";

export function Checkin({ tasks, date }: { tasks: any[]; date: string }) {
  const [localTasks, setLocalTasks] = useState<any[]>(tasks);
  const [showForm, setShowForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState<"care" | "work" | "personal">("work");

  const handleToggle = async (id: number) => {
    try {
      await toggleTask(id);
      setLocalTasks(localTasks.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
    } catch {
      alert("Failed to update task");
    }
  };

  const handleMove = async (id: number) => {
    if (!date) return;
    const tomorrow = new Date(date + "T00:00:00");
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextDate = tomorrow.toISOString().split("T")[0];
    if (!nextDate) return;
    try {
      await moveTask(id, nextDate);
      setLocalTasks(localTasks.filter((t) => t.id !== id));
    } catch {
      alert("Failed to move task");
    }
  };

  const handleDrop = async (id: number) => {
    try {
      await dropTask(id);
      setLocalTasks(localTasks.filter((t) => t.id !== id));
    } catch {
      alert("Failed to drop task");
    }
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    try {
      const result = await createTask({
        date,
        label: newLabel.trim(),
        category: newCategory,
      });
      setLocalTasks([...localTasks, { id: result.id, label: newLabel, category: newCategory, done: false, date }]);
      setNewLabel("");
      setShowForm(false);
    } catch {
      alert("Failed to add task");
    }
  };

  const care = localTasks.filter((t) => t.category === "care");
  const work = localTasks.filter((t) => t.category === "work");
  const personal = localTasks.filter((t) => t.category === "personal");

  const TaskItem = ({ t }: { t: any }) => (
    <Card style={{ padding: "10px 13px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <button
            onClick={() => handleToggle(t.id)}
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              border: `2px solid ${t.done ? T.mint : T.dim}`,
              background: t.done ? T.mint : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {t.done && <Check size={12} color={T.night} />}
          </button>
          <span
            style={{
              fontSize: 13,
              color: t.done ? T.dim : T.paper,
              fontWeight: 600,
              textDecoration: t.done ? "line-through" : "none",
              flex: 1,
            }}
          >
            {t.label}
          </span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            onClick={() => handleMove(t.id)}
            style={{
              background: "transparent",
              border: `1px solid ${T.edge}`,
              borderRadius: 6,
              padding: "3px 8px",
              color: T.gold,
              fontSize: 10,
              cursor: "pointer",
            }}
          >
            Move
          </button>
          <button
            onClick={() => handleDrop(t.id)}
            style={{
              background: "transparent",
              border: "none",
              color: T.dim,
              cursor: "pointer",
              padding: 2,
            }}
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </Card>
  );

  return (
    <div style={{ padding: "18px 16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Eyebrow>check-in · {date}</Eyebrow>
        <Moon size={14} color={T.mist} />
      </div>

      <div style={{ margin: "12px 0 4px", fontSize: 18, color: T.paper, fontWeight: 650 }}>Today's tasks</div>

      <button
        onClick={() => setShowForm(!showForm)}
        style={{
          background: "transparent",
          border: `1px solid ${T.edge}`,
          borderRadius: 8,
          padding: "6px 10px",
          color: T.mist,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          marginBottom: 12,
        }}
      >
        <Plus size={12} /> {showForm ? "Cancel" : "Add task"}
      </button>

      {showForm && (
        <Card style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            placeholder="What needs doing?"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            style={{
              background: "rgba(0,0,0,0.3)",
              border: `1px solid ${T.edge}`,
              borderRadius: 6,
              padding: "6px 10px",
              color: T.paper,
              fontSize: 12,
              outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            {(["care", "work", "personal"] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setNewCategory(cat)}
                style={{
                  flex: 1,
                  padding: "5px 0",
                  borderRadius: 6,
                  border: `1px solid ${newCategory === cat ? T.gold : T.edge}`,
                  background: newCategory === cat ? "rgba(255,180,84,.15)" : "transparent",
                  color: newCategory === cat ? T.gold : T.mist,
                  fontSize: 10,
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {cat}
              </button>
            ))}
          </div>
          <button
            onClick={handleAdd}
            disabled={!newLabel.trim()}
            style={{
              background: newLabel.trim() ? T.gold : T.dim,
              border: "none",
              borderRadius: 8,
              padding: "8px 12px",
              color: T.night,
              fontWeight: 600,
              fontSize: 12,
              cursor: newLabel.trim() ? "pointer" : "not-allowed",
            }}
          >
            Add
          </button>
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {localTasks.length === 0 && (
          <div style={{ textAlign: "center", padding: "12px 0", color: T.dim, fontSize: 12 }}>
            No tasks yet. Add one above.
          </div>
        )}

        {care.length > 0 && (
          <>
            <Eyebrow>care</Eyebrow>
            {care.map((t) => (
              <TaskItem key={t.id} t={t} />
            ))}
          </>
        )}

        {work.length > 0 && (
          <>
            <div style={{ marginTop: 8 }}>
              <Eyebrow>work</Eyebrow>
            </div>
            {work.map((t) => (
              <TaskItem key={t.id} t={t} />
            ))}
          </>
        )}

        {personal.length > 0 && (
          <>
            <div style={{ marginTop: 8 }}>
              <Eyebrow>personal</Eyebrow>
            </div>
            {personal.map((t) => (
              <TaskItem key={t.id} t={t} />
            ))}
          </>
        )}
      </div>

      <Card style={{ marginTop: 14, borderColor: "rgba(130,220,164,.3)" }}>
        <Eyebrow color={T.mint}>sleep</Eyebrow>
        <div style={{ fontSize: 12, color: T.mist, marginTop: 6 }}>
          <Sunrise size={13} color={T.gold} style={{ display: "inline", marginRight: 6 }} />
          Target bedtime for your routine
        </div>
      </Card>
    </div>
  );
}
